import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Button, Card, EmptyState, Skeleton } from '../primitives';
import { supabase } from '../../lib/supabase';
import {
  AttachDocumentCover,
  type AttachDocumentLifecycle,
  type AttachDocumentRow
} from '../AttachDocumentCover';

export interface AttachedDocumentsPanelProps {
  workItemId: string;
  orgId: string;
  onError: (message: string) => void;
  /**
   * When true the panel renders only its inner list — the parent supplies
   * the chrome (border, padding, title). The "+ Attach Document" action
   * moves inline above the list because there's no Card head to host it.
   */
  naked?: boolean;
}

interface LinkedDocument {
  id: string;
  title: string;
  lifecycle: AttachDocumentLifecycle;
  updatedAt: string;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; rows: LinkedDocument[] };

function coerceLifecycle(value: string | null | undefined): AttachDocumentLifecycle {
  return value === 'published' || value === 'archived' ? value : 'draft';
}

export function AttachedDocumentsPanel({
  workItemId,
  orgId,
  onError,
  naked = false
}: AttachedDocumentsPanelProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [coverOpen, setCoverOpen] = useState(false);
  const [candidates, setCandidates] = useState<AttachDocumentRow[]>([]);
  const [refreshTick, setRefreshTick] = useState(0);
  // Cover opens in viewer mode when a user clicks an attached-doc row, so
  // they can edit the document inline without losing work-item context.
  const [viewerDocId, setViewerDocId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      // NOTE: `work_item_document_links` has no `org_id` column (it's a pure
      // junction of work_item_id ↔ document_id; verified against the live
      // schema). Org scoping is transitive — `work_item_id` is itself org-owned
      // and the client can only have obtained an in-org id, so filtering by it
      // already constrains the read to the active org.
      const { data, error } = await supabase
        .from('work_item_document_links')
        .select(
          'document_id, documents(id, title, last_event_at)'
        )
        .eq('work_item_id', workItemId);
      if (cancelled) return;
      if (error) {
        setLoad({ kind: 'error', message: error.message });
        return;
      }
      const rows: LinkedDocument[] = (data ?? [])
        .map((row) => {
          const embedded = (row as unknown as {
            documents:
              | {
                  id: string;
                  title: string;
                  last_event_at: string;
                }
              | Array<{
                  id: string;
                  title: string;
                  last_event_at: string;
                }>
              | null;
          }).documents;
          const doc = Array.isArray(embedded) ? embedded[0] ?? null : embedded;
          if (!doc) return null;
          // Phase 3: lifecycle column dropped — placeholder until Phase 5
          // resolves via custom_attribute_values join.
          return {
            id: doc.id,
            title: doc.title,
            lifecycle: coerceLifecycle(null),
            updatedAt: doc.last_event_at
          };
        })
        .filter((r): r is LinkedDocument => r !== null)
        .sort((a, b) => a.title.localeCompare(b.title));
      setLoad({ kind: 'ready', rows });
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, refreshTick]);

  const linkedIds = useMemo(() => {
    if (load.kind !== 'ready') return new Set<string>();
    return new Set(load.rows.map((r) => r.id));
  }, [load]);

  const openCover = useCallback(async () => {
    const { data, error } = await supabase
      .from('documents')
      .select('id, title, last_event_at, body')
      .eq('org_id', orgId)
      .order('last_event_at', { ascending: false });
    if (error) {
      onError(error.message);
      return;
    }
    // Phase 3: lifecycle column dropped — placeholder until Phase 5 resolves
    // via custom_attribute_values join.
    const rows: AttachDocumentRow[] = (data ?? [])
      .filter((d) => !linkedIds.has(d.id as string))
      .map((d) => ({
        id: d.id as string,
        title: (d.title as string) ?? '',
        lifecycle: coerceLifecycle(null),
        ownerName: null,
        updatedAt: (d.last_event_at as string) ?? new Date().toISOString(),
        bodyExcerpt: ((d.body as string) ?? '').slice(0, 240)
      }));
    setCandidates(rows);
    setCoverOpen(true);
  }, [orgId, linkedIds, onError]);

  const handleAttach = useCallback(
    async (docIds: string[]) => {
      if (docIds.length === 0) return;
      // No `org_id` on this junction table — the link rows reference a
      // work_item_id + document_id the client could only have obtained in-org,
      // so the insert is already org-gated without an explicit column.
      const rows = docIds.map((docId) => ({
        work_item_id: workItemId,
        document_id: docId
      }));
      const { error } = await supabase
        .from('work_item_document_links')
        .insert(rows);
      if (error) {
        onError(error.message);
        return;
      }
      setRefreshTick((t) => t + 1);
    },
    [workItemId, onError]
  );

  const handleUnlink = useCallback(
    async (docId: string, title: string) => {
      if (
        !window.confirm(
          `Unlink "${title || 'document'}" from this work item? The document still exists in /documents.`
        )
      ) {
        return;
      }
      const { error } = await supabase
        .from('work_item_document_links')
        .delete()
        .eq('work_item_id', workItemId)
        .eq('document_id', docId);
      if (error) {
        onError(error.message);
        return;
      }
      setRefreshTick((t) => t + 1);
    },
    [workItemId, onError]
  );

  const attachAction = (
    <button
      type="button"
      className="pn-card-head-action"
      onClick={() => void openCover()}
      data-testid="work-item-detail-attached-documents-attach"
    >
      + Attach Document
    </button>
  );

  // Naked mode has no Card head, so the "+ Attach" action moves into a
  // leading inline row. The legacy variant keeps it in Card.Head.actions.
  const nakedAttachRow = (
    <div className="wid-attached-naked-actions">
      <button
        type="button"
        className="wid-attached-naked-attach"
        onClick={() => void openCover()}
        data-testid="work-item-detail-attached-documents-attach"
      >
        + Attach Document
      </button>
    </div>
  );

  const wrap = (
    state: 'loading' | 'error' | 'ready',
    body: ReactNode,
    rowCount?: number
  ) => {
    if (naked) {
      return (
        <div
          className="wid-attached-naked"
          data-testid="work-item-detail-attached-documents"
          data-state={state}
          data-row-count={rowCount}
          aria-label="Attached documents"
        >
          {nakedAttachRow}
          {body}
          <AttachDocumentCover
            open={coverOpen}
            onClose={() => setCoverOpen(false)}
            onAttach={handleAttach}
            workItemId={workItemId}
            documents={candidates}
          />
          {viewerDocId ? (
            <AttachDocumentCover
              mode="viewer"
              open
              onClose={() => {
                setViewerDocId(null);
                setRefreshTick((t) => t + 1);
              }}
              workItemId={workItemId}
              docId={viewerDocId}
            />
          ) : null}
        </div>
      );
    }
    return (
      <Card
        data-testid="work-item-detail-attached-documents"
        data-state={state}
        data-row-count={rowCount}
        aria-label="Attached documents"
      >
        <Card.Head
          title="Attached documents"
          grow
          actions={state === 'ready' ? attachAction : undefined}
        />
        <Card.Body>{body}</Card.Body>
        <AttachDocumentCover
          open={coverOpen}
          onClose={() => setCoverOpen(false)}
          onAttach={handleAttach}
          workItemId={workItemId}
          documents={candidates}
        />
        {viewerDocId ? (
          <AttachDocumentCover
            mode="viewer"
            open
            onClose={() => {
              setViewerDocId(null);
              setRefreshTick((t) => t + 1);
            }}
            workItemId={workItemId}
            docId={viewerDocId}
          />
        ) : null}
      </Card>
    );
  };

  if (load.kind === 'loading') {
    return wrap('loading', <Skeleton width="100%" height={32} />);
  }

  if (load.kind === 'error') {
    return wrap(
      'error',
      <div className="wid-attached-docs-error">{load.message}</div>
    );
  }

  const body =
    load.rows.length === 0 ? (
      <EmptyState
        data-testid="work-item-detail-attached-documents-empty"
        action={
          <Button
            variant="primary"
            size="sm"
            onClick={() => void openCover()}
            data-testid="work-item-detail-attached-documents-empty-attach"
          >
            + Attach Document
          </Button>
        }
      >
        No documents attached yet.
      </EmptyState>
    ) : (
      <ul className="wid-child-list">
        {load.rows.map((row) => (
          <li
            key={row.id}
            className="wid-child-row"
            data-testid={`work-item-detail-attached-documents-row-${row.id}`}
          >
            <span className="wid-child-row-kind">DOC</span>
            <button
              type="button"
              className="wid-child-row-title"
              onClick={() => setViewerDocId(row.id)}
              data-testid={`work-item-detail-attached-documents-title-${row.id}`}
            >
              {row.title || 'Untitled'}
            </button>
            <span
              className="wid-child-row-status"
              data-lifecycle={row.lifecycle}
            >
              {row.lifecycle}
            </span>
            <button
              type="button"
              className="wid-row-unlink"
              onClick={() => void handleUnlink(row.id, row.title)}
              aria-label={`Unlink ${row.title || 'document'} from this work item`}
              title="Unlink (does not delete the document)"
              data-testid={`work-item-detail-attached-documents-unlink-${row.id}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
    );

  return wrap('ready', body, load.rows.length);
}
